import React, {useEffect, useRef, useState} from 'react';
import { Template } from "../../../components/template/template.jsx";
import "../../../css/style-report.css";
import "../../../css/input-date-time.css";
import ReportUtils from "../../../utils/report-utils.js";
import axios from "axios";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import {Card} from "../../../components/card/card.jsx";
import Content from "./components/content.jsx";
import {subMonths} from "date-fns";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import Filters from "./components/filters.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import {useSearchParams} from "react-router";

const API_URL = import.meta.env.VITE_API_URL;

const initialFilters = () => ({
    dtStart: subMonths(new Date(), 3),
    dtEnd: new Date(),
    hrStart: new Date(new Date().setHours(0, 0)),
    hrEnd: new Date(new Date().setHours(23, 59)),
    plate: null,
    period: {label: "Todos", value: "all"},
    pcl: [],
    alarmType: {label: "Todos", value: "all"},
    questions: [],
    archivedOccurrences: true,
    excludeAlarmsAutomaticallySigned: true,
    onlyPlateIsExact: false,
});

const filterLabels = {
    dtStart: "Data inicial",
    dtEnd: "Data final",
    hrStart: "Hora inicial",
    hrEnd: "Hora final",
    plate: "Placa",
    period: "Período",
    pcl: "Pontos de coleta",
    alarmType: "Origem",
    questions: "Perguntas de alarmes",
    archivedOccurrences: "Contabilizar ocorrências arquivadas",
    excludeAlarmsAutomaticallySigned: "Excluir alarmes assinados automaticamente",
    onlyPlateIsExact: "Alarmes que a leitura da placa confere com a imagem",
};

const AlarmStatistics = () => {
    const [loading, setLoading] = useState(false);
    const [searchParams] = useSearchParams();
    const dtStart = searchParams.get("dtStart");
    const dtEnd = searchParams.get("dtEnd");
    const plate = searchParams.get("plate");

    const [collapsed, setCollapsed] = useState(false);
    const buttonRef = useRef(null);
    const [fixedTop, setFixedTop] = useState(null);

    const [filters, setFilters] = useState(() => {
        const initial = initialFilters();

        return {
            ...initial,
            dtStart: dtStart ? new Date(`${dtStart}T00:00:00`) : initial.dtStart,
            dtEnd: dtEnd ? new Date(`${dtEnd}T00:00:00`) : initial.dtEnd,
            plate: plate ?? initial.plate,
        };
    });

    const [appliedFilters, setAppliedFilters] = useState(initialFilters);
    const [reportData, setReportData] = useState(null);

    const [title] = useState("Estatísticas de alarmes");
    const [headerSelected, setHeaderSelected] = useState(-1);

    useEffect(() => {
        if (!reportData) return;

        ReportUtils.appendFilters(
            ReportUtils.filters({
                ...filters,
                dtStart: filters.dtStart ? ReportUtils.formatDateOnly(filters.dtStart) : "",
                dtEnd: filters.dtEnd ? ReportUtils.formatDateOnly(filters.dtEnd) : "",
                hrStart: filters.hrStart ? ReportUtils.formatHourMin(filters.hrStart, false) : "",
                hrEnd: filters.hrEnd ? ReportUtils.formatHourMin(filters.hrEnd, false) : "",
            }, filterLabels)
        );

    }, [reportData]);

    const apply = async () => {
        ReportUtils.applyLoad({
            setLoading,
            clearContent: () => {
                setReportData(null)
            }
        });

        try {
            let periodValidation;
            if (!filters.plate) {
                periodValidation = ReportUtils.validateOnlyDates(filters.dtStart, filters.dtEnd, 3, 'months');

                if (!periodValidation.ok) {
                    ReportUtils.alertErrorMessage('O período máximo de pesquisa é de 3 meses, digite uma placa para pesquisar até 1 ano.');
                    setLoading(false);
                    return;
                }
            } else {
                periodValidation = ReportUtils.validateOnlyDates(filters.dtStart, filters.dtEnd, 1, 'year');

                if (!periodValidation.ok) {
                    ReportUtils.alertErrorMessage(periodValidation.message);
                    setLoading(false);
                    return;
                }
            }

            if (!ReportUtils.validateHourOnly(filters.hrStart, filters.hrEnd, setLoading)) return;

            const formattedFilters = {
                dtStart: filters.dtStart ? ReportUtils.formatDateOnly(filters.dtStart) : null,
                dtEnd: filters.dtEnd ? ReportUtils.formatDateOnly(filters.dtEnd) : null,
                hrStart: filters.hrStart ? ReportUtils.formatHourMin(filters.hrStart, false) : null,
                hrEnd: filters.hrEnd ? ReportUtils.formatHourMin(filters.hrEnd, false) : null,
                plate: filters.plate ?? null,
                period: filters.period?.value ?? 'all',
                pcl: filters.pcl?.map(i => i.value) ?? [],
                alarmType: filters.alarmType?.value ?? 'all',
                questions: filters.questions?.map(i => i.value) ?? [],
                archivedOccurrences: filters.archivedOccurrences ?? false,
                excludeAlarmsAutomaticallySigned: filters.excludeAlarmsAutomaticallySigned ?? false,
                onlyPlateIsExact: filters.onlyPlateIsExact ?? false
            };

            const auditFilters = ReportUtils.filters({
                ...filters,
                dtStart: formattedFilters.dtStart,
                dtEnd: formattedFilters.dtEnd,
                hrStart: formattedFilters.hrStart,
                hrEnd: formattedFilters.hrEnd,
            }, filterLabels, true);

            const { data: response } = await axios.post(API_URL + "/alarm_statistics/apply", {
                ...formattedFilters,
                filters: auditFilters
            });

            const data = response.data;
            setReportData(data);
            setAppliedFilters(filters);

            const scale = window.visualViewport?.scale ?? 1;
            if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

        } catch (error) {

            ReportUtils.alertErrorMessage("Erro ao gerar o relatório.");
            console.error("Erro ao gerar o relatório", error);

        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!dtStart && !dtEnd && !plate) return;
        apply();
    }, []);

    useGlobalKey("Enter", apply);

    const clearSearch = () => {
        ReportUtils.clearUrlFilters();
        setFilters(initialFilters);
        setReportData(null);
    }

    const toggleFilters = () => {
        if (!collapsed && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setFixedTop(rect.top);
        }

        setCollapsed(prev => !prev);
    }

    const printComponentRef = useRef(null);

    const hasData = (() => {
        try {
            return Object.values(JSON.parse(reportData?.apply?.cards?.[0]?.text ?? "{}")).some(v => v > 0);
        } catch {
            return false;
        }
    })();

    return (
        <div className={"flex"}>

            <Template isLoading={loading}>
                <Template.Header>
                    <div className="flex justify-between w-full">
                        <Template.Title>{title}</Template.Title>
                        <HeaderButtons
                            exportable={true}
                            exportInSheets={true}
                            printable={true}
                            headerChange={true}
                            printComponentRef={printComponentRef}
                            reportData={reportData}
                            data={hasData}
                            headerSelected={headerSelected}
                            setHeaderSelected={setHeaderSelected}
                            setCollapsed={setCollapsed}
                        />
                    </div>
                </Template.Header>
                <Template.Body className={`${collapsed ? 'p-[0]' : ''}`}>
                    <Menu buttonRef={buttonRef} toggleFilters={toggleFilters} fixedTop={fixedTop}
                          collapsed={collapsed} apply={apply} clearSearch={clearSearch}>

                        <Filters setFilters={setFilters} filters={filters}/>
                        <AutoDatetime setFilters={setFilters} hasSeconds={false}/>

                    </Menu>
                </Template.Body>
            </Template>

            <Card className={`w-full h-fit mr-2 ${!reportData || !hasData ? 'mt-[59px]' : 'mt-[61px]' } print:!mt-[-15px] transform ${!reportData || !hasData ? '!bg-[#f3f3f4] !border-none !-translate-y-[5px]' : ''} ${collapsed ? 'ml-2' : ''}`}>
                <Card.Body>
                    <div className={`flex-1 ${!reportData || !hasData ? 'p-[0]' : 'px-[20px] py-[5px]'}`}>
                        <NotAppliedContent reportData={reportData}/>
                        <NoDataContent reportData={reportData} data={!hasData && 0}/>
                        <Content reportData={reportData} data={hasData && 1} startDate={appliedFilters.dtStart} endDate={appliedFilters.dtEnd} entity={headerSelected} title={title} collapsed={collapsed} setLoading={setLoading} printComponentRef={printComponentRef}/>
                    </div>
                </Card.Body>
            </Card>

        </div>
    );
};

export default AlarmStatistics;